/**
 * Helpers for locating a group ride's Race Sector along its route and elevation profile.
 */
import type { Athlete, ElevationPoint, MatchEvent, SpiritedSegment } from '../types';

const findNearestIndex = (lat: number, lon: number, points: { lat: number; lon: number }[]): number => {
  let bestIdx = -1;
  let minDistSq = Infinity;
  for (let i = 0; i < points.length; i++) {
    const dLat = points[i].lat - lat;
    const dLon = points[i].lon - lon;
    const distSq = dLat * dLat + dLon * dLon;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      bestIdx = i;
    }
  }
  return bestIdx;
};

const getDistanceMeters = (p1: [number, number], p2: [number, number]): number => {
  const R = 6371000;
  const dLat = (p2[0] - p1[0]) * Math.PI / 180;
  const dLon = (p2[1] - p1[1]) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(p1[0] * Math.PI / 180) * Math.cos(p2[0] * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const hasSector = (sector?: SpiritedSegment): sector is SpiritedSegment =>
  !!(sector && sector.startLat && sector.startLon && sector.finishLat && sector.finishLon);

/**
 * Returns the [startIdx, endIdx] range of elevation profile points covered by the Race Sector.
 */
export const getSectorProfileRange = (
  profile: ElevationPoint[] | undefined,
  sector: SpiritedSegment | undefined
): { startIdx: number; endIdx: number } | null => {
  if (!profile || profile.length < 2 || !hasSector(sector)) return null;
  const s = findNearestIndex(sector.startLat, sector.startLon, profile);
  const f = findNearestIndex(sector.finishLat, sector.finishLon, profile);
  if (s === -1 || f === -1 || s === f) return null;
  return { startIdx: Math.min(s, f), endIdx: Math.max(s, f) };
};

/**
 * Normalizes a Race Sector so that `startLat/Lon` comes before `finishLat/Lon`
 * along the course path. If the host picked markers in reverse of the route flow,
 * this orients the sector in the intended ride direction.
 */
export const normalizeSector = (
  sector: SpiritedSegment | undefined,
  coursePath?: [number, number][]
): SpiritedSegment | undefined => {
  if (!hasSector(sector)) return sector;
  if (!coursePath || coursePath.length < 2) return sector;
  const pathPoints = coursePath.map(([lat, lon]) => ({ lat, lon }));
  const s = findNearestIndex(sector.startLat, sector.startLon, pathPoints);
  const f = findNearestIndex(sector.finishLat, sector.finishLon, pathPoints);
  if (s !== -1 && f !== -1 && s > f) {
    return {
      startLat: sector.finishLat,
      startLon: sector.finishLon,
      finishLat: sector.startLat,
      finishLon: sector.startLon,
    };
  }
  return sector;
};

/**
 * Returns the slice of the course path covered by the Race Sector.
 */
export const getSectorPathSlice = (
  coursePath: [number, number][] | undefined,
  sector: SpiritedSegment | undefined
): [number, number][] => {
  if (!coursePath || coursePath.length < 2 || !hasSector(sector)) return [];
  const pathPoints = coursePath.map(([lat, lon]) => ({ lat, lon }));
  const s = findNearestIndex(sector.startLat, sector.startLon, pathPoints);
  const f = findNearestIndex(sector.finishLat, sector.finishLon, pathPoints);
  if (s === -1 || f === -1 || s === f) return [];
  return coursePath.slice(Math.min(s, f), Math.max(s, f) + 1);
};

/**
 * Computes Race Sector distance (from the full-resolution course path when available)
 * and elevation gain (from the elevation profile).
 */
export const computeSectorStats = (
  coursePath: [number, number][] | undefined,
  profile: ElevationPoint[] | undefined,
  sector: SpiritedSegment | undefined
): { distanceMiles: number | null; elevationGainFt: number | null } | null => {
  if (!hasSector(sector)) return null;

  let distanceMiles: number | null = null;
  if (coursePath && coursePath.length > 1) {
    const pathPoints = coursePath.map(([lat, lon]) => ({ lat, lon }));
    const s = findNearestIndex(sector.startLat, sector.startLon, pathPoints);
    const f = findNearestIndex(sector.finishLat, sector.finishLon, pathPoints);
    let meters = 0;
    for (let i = Math.min(s, f); i < Math.max(s, f); i++) {
      meters += getDistanceMeters(coursePath[i], coursePath[i + 1]);
    }
    distanceMiles = Number((meters * 0.000621371).toFixed(2));
  } else {
    distanceMiles = Number((getDistanceMeters(
      [sector.startLat, sector.startLon],
      [sector.finishLat, sector.finishLon]
    ) * 0.000621371).toFixed(2));
  }

  let elevationGainFt: number | null = null;
  const range = getSectorProfileRange(profile, sector);
  if (range && profile) {
    let gain = 0;
    for (let i = range.startIdx + 1; i <= range.endIdx; i++) {
      const diff = profile[i].elevationFt - profile[i - 1].elevationFt;
      if (diff > 0) gain += diff;
    }
    elevationGainFt = Math.round(gain);
  }

  return { distanceMiles, elevationGainFt };
};

// --- Per-rider Race Sector efforts (used by the visualizers) ---

/** A rider must pass within this distance of a sector marker to count as crossing it. */
export const SECTOR_GATE_RADIUS_METERS = 50;

interface EffortTrackPoint {
  lat: number;
  lon: number;
  timestamp: number;
  distance: number;
  power?: number | null;
  heartRate?: number | null;
}

export interface SectorEffort {
  elapsedMs: number;
  startTs: number;
  endTs: number;
  distanceM: number;
  avgSpeedMps: number;
  avgPower: number | null;
  avgHr: number | null;
}

/**
 * Index of the closest point to `target` during the first visit inside the gate radius,
 * searching from `fromIdx`. Returns -1 if the track never enters the gate.
 */
const findGateCrossing = (track: EffortTrackPoint[], target: [number, number], fromIdx: number): number => {
  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = fromIdx; i < track.length; i++) {
    const d = getDistanceMeters([track[i].lat, track[i].lon], target);
    if (d <= SECTOR_GATE_RADIUS_METERS) {
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    } else if (bestIdx !== -1) {
      break; // left the gate after the first visit
    }
  }
  return bestIdx;
};

const average = (values: (number | null | undefined)[]): number | null => {
  const nums = values.filter((v): v is number => typeof v === 'number' && !isNaN(v));
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
};

/**
 * A rider's first complete pass through the Race Sector (start gate, then end gate).
 * Directional: a rider who reaches the end gate before the start gate has no effort.
 */
export const computeSectorEffort = (
  track: EffortTrackPoint[] | undefined,
  sector: SpiritedSegment | undefined
): SectorEffort | null => {
  if (!track || track.length < 2 || !hasSector(sector)) return null;

  const startIdx = findGateCrossing(track, [sector.startLat, sector.startLon], 0);
  if (startIdx === -1) return null;
  const endIdx = findGateCrossing(track, [sector.finishLat, sector.finishLon], startIdx + 1);
  if (endIdx === -1 || endIdx <= startIdx) return null;

  const start = track[startIdx];
  const end = track[endIdx];
  const elapsedMs = end.timestamp - start.timestamp;
  if (elapsedMs <= 0) return null;

  const distanceM = Math.max(0, end.distance - start.distance);
  const window = track.slice(startIdx, endIdx + 1);

  return {
    elapsedMs,
    startTs: start.timestamp,
    endTs: end.timestamp,
    distanceM,
    avgSpeedMps: distanceM / (elapsedMs / 1000),
    avgPower: average(window.map(p => p.power)),
    avgHr: average(window.map(p => p.heartRate)),
  };
};

export interface SectorLeaderboardRow {
  athleteId: string;
  name: string;
  color: string;
  userId?: string;
  rank: number;
  gapMs: number;
  effort: SectorEffort;
}

/** Ranks riders by sector time. Riders without a complete pass are returned separately. */
export const computeSectorLeaderboard = (
  athletes: { id: string; name: string; color: string; userId?: string; track: EffortTrackPoint[] }[],
  sector: SpiritedSegment | undefined,
  coursePath?: [number, number][]
): { rows: SectorLeaderboardRow[]; incomplete: { athleteId: string; name: string }[] } => {
  const effectiveSector = normalizeSector(sector, coursePath);
  const withEfforts = athletes.map(a => ({ a, effort: computeSectorEffort(a.track, effectiveSector) }));
  const completed = withEfforts
    .filter((x): x is { a: typeof x.a; effort: SectorEffort } => x.effort !== null)
    .sort((x, y) => x.effort.elapsedMs - y.effort.elapsedMs);
  const bestMs = completed[0]?.effort.elapsedMs ?? 0;

  return {
    rows: completed.map(({ a, effort }, i) => ({
      athleteId: a.id,
      name: a.name,
      color: a.color,
      userId: a.userId,
      rank: i + 1,
      gapMs: effort.elapsedMs - bestMs,
      effort,
    })),
    incomplete: withEfforts.filter(x => x.effort === null).map(x => ({ athleteId: x.a.id, name: x.a.name })),
  };
};

export const formatSectorTime = (ms: number): string => {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}` : `${m}:${s.toString().padStart(2, '0')}`;
};

export interface SectorAthleteTelemetry {
  effort: SectorEffort;
  maxPower: number | null;
  maxSpeedMph: number;
  sectorMatches: MatchEvent[];
  preSectorMatches: MatchEvent[];
  preSectorAvgPower: number | null;
  postSectorMatches: MatchEvent[];
  weightKg: number | null;
  sectorAvgWkg: number | null;
  sectorMaxWkg: number | null;
}

export const getSectorAthleteTelemetry = (
  athlete: Athlete,
  sector: SpiritedSegment | undefined,
  coursePath?: [number, number][],
  profileWeightLbs?: number | null
): SectorAthleteTelemetry | null => {
  const effectiveSector = normalizeSector(sector, coursePath);
  const effort = computeSectorEffort(athlete.track, effectiveSector);
  if (!effort) return null;

  const weightKg = athlete.weight || (profileWeightLbs ? profileWeightLbs / 2.20462 : null);

  let maxPower = 0;
  let maxSpeedKmh = 0;
  let prePowerSum = 0;
  let prePowerCount = 0;

  athlete.track.forEach(pt => {
    if (pt.timestamp >= effort.startTs && pt.timestamp <= effort.endTs) {
      if (pt.power !== null && pt.power !== undefined && pt.power > maxPower) {
        maxPower = pt.power;
      }
      if (pt.speed > maxSpeedKmh) {
        maxSpeedKmh = pt.speed;
      }
    } else if (pt.timestamp < effort.startTs) {
      if (pt.power !== null && pt.power !== undefined && pt.power > 0) {
        prePowerSum += pt.power;
        prePowerCount++;
      }
    }
  });

  const matches = athlete.matchEvents || [];
  const sectorMatches = matches.filter(m => m.time >= effort.startTs && m.time <= effort.endTs);
  const preSectorMatches = matches.filter(m => m.time < effort.startTs);
  const postSectorMatches = matches.filter(m => m.time > effort.endTs);

  const sectorAvgWkg = weightKg && effort.avgPower ? Number((effort.avgPower / weightKg).toFixed(1)) : null;
  const sectorMaxWkg = weightKg && maxPower > 0 ? Number((maxPower / weightKg).toFixed(1)) : null;
  const preSectorAvgPower = prePowerCount > 0 ? Math.round(prePowerSum / prePowerCount) : null;
  const maxSpeedMph = Number((maxSpeedKmh * 0.621371).toFixed(1));

  return {
    effort,
    maxPower: maxPower > 0 ? maxPower : null,
    maxSpeedMph,
    sectorMatches,
    preSectorMatches,
    preSectorAvgPower,
    postSectorMatches,
    weightKg,
    sectorAvgWkg,
    sectorMaxWkg,
  };
};
